import { makeStyles, Popover, PopoverSurface, PopoverTrigger, tokens } from '@fluentui/react-components';
import React, { useState } from 'react';
import { customPalette } from '../../../theme';

export interface IFilterPickerProps {
    /** Controlled open state. Omit for uncontrolled. */
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Called when the picker closes (open becomes false). */
    onDismiss?: () => void;

    surfaceAriaLabel?: string;
    contentAriaLabel?: string;
    onContentKeyDown?: (event: React.KeyboardEvent) => void;

    /** Trigger slot — typically a Fluent Button. Wrapped in `PopoverTrigger`. */
    trigger: React.ReactElement;
    /** Optional tab list above the search header. */
    tabs?: React.ReactNode;
    /** Live-region text when the filtered result set changes. */
    announcementMessage?: string;
    /** Search slot — typically `FilterSearchBar`. */
    search?: React.ReactNode;
    /** Clear / view-all control below search. */
    clearSelection?: React.ReactNode;
    /** Scrollable list body. */
    list: React.ReactNode;
    /** Optional footer (e.g. New folder, Apply/Cancel). */
    footer?: React.ReactNode;
    /** When true, PopoverSurface traps focus and acts as a modal. Default true. */
    trapFocus?: boolean;
    /** Extra props spread onto the scrollable content root (`#filterOptionsContainer`). */
    contentRootProps?: React.ComponentPropsWithoutRef<'div'>;
}

export const useFilterPickerStyles = makeStyles({
    activeTrigger: {
        backgroundColor: tokens.colorBrandBackground2,
        border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke1}`,
        ':hover:not(:disabled):not([aria-disabled="true"])': {
            backgroundColor: tokens.colorBrandBackground2Hover,
            border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke1}`,
        },
        ':hover:active:not(:disabled):not([aria-disabled="true"])': {
            backgroundColor: tokens.colorBrandBackground2Pressed,
            border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke1}`,
        },
    },
    openTrigger: {
        backgroundColor: customPalette.statusCoolLightGrey,
    },
    surface: {
        padding: 0,
        maxWidth: '540px',
        width: '540px',
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow16,
    },
    contentContainer: {
        maxHeight: '25vmax',
        display: 'flex',
        flexDirection: 'column',
        width: '540px',
        // the old design had extra padding above
        paddingTop: '8px',
    },
    filterItemsScrollable: {
        overflowX: 'hidden',
        overflowY: 'auto',
    },
    menuHeaderRow: {
        position: 'sticky',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '4px 10px',
        gap: '10px',
        width: '100%',
        backgroundColor: tokens.colorNeutralBackground1,
        flex: 'none',
        zIndex: 1,
    },
    announced: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
    noResults: {
        padding: '4px',
        color: customPalette.blueDark,
    },
    list: {
        margin: 0,
        padding: 0,
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        minHeight: '36px',
        paddingLeft: '24px',
        paddingRight: '10px',
        color: customPalette.blueDark,
        fontSize: '13px',
        fontWeight: tokens.fontWeightSemibold,
    },
    listItemChecked: {
        '@media (forced-colors: none)': {
            color: customPalette.themePrimary,
        },
        '@media (forced-colors: active)': {
            border: '1px solid',
        },
    },
    footerContainer: { width: '100%' },
});

/**
 * Fluent-style searchable picker shell with slot props on a single root.
 * Call sites compose `trigger`, `search`, `list`, and optional `footer` / `clearSelection`.
 * Prefer Fluent `List` / `ListItem` in the `list` slot so typography follows FluentProvider.
 */
const FilterPicker: React.FC<IFilterPickerProps> = ({
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    onDismiss,
    surfaceAriaLabel,
    contentAriaLabel,
    onContentKeyDown,
    trigger,
    tabs,
    announcementMessage,
    search,
    clearSelection,
    list,
    footer,
    trapFocus = true,
    contentRootProps,
}) => {
    const styles = useFilterPickerStyles();
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const isControlled = openProp !== undefined;
    const isOpen = isControlled ? openProp : uncontrolledOpen;

    const setOpen = (next: boolean) => {
        if (!isControlled) {
            setUncontrolledOpen(next);
        }
        onOpenChange?.(next);
        if (!next) {
            onDismiss?.();
        }
    };

    const hasHeader = Boolean(search || clearSelection || (announcementMessage && announcementMessage.trim() !== ''));

    return (
        <Popover
            open={isOpen}
            onOpenChange={(_ev, data) => setOpen(data.open)}
            positioning={{ position: 'below', align: 'start', offset: { mainAxis: 10 } }}
            trapFocus={trapFocus}
        >
            <PopoverTrigger disableButtonEnhancement>{trigger}</PopoverTrigger>
            <PopoverSurface className={styles.surface} aria-label={surfaceAriaLabel}>
                <div
                    id='filterOptionsContainer'
                    data-testid='filter-options-container'
                    className={styles.contentContainer}
                    onKeyDown={onContentKeyDown}
                    tabIndex={0}
                    aria-label={contentAriaLabel ?? surfaceAriaLabel}
                    {...contentRootProps}
                >
                    {tabs}
                    {hasHeader ? (
                        <div className={styles.menuHeaderRow}>
                            {announcementMessage && announcementMessage.trim() !== '' ? (
                                <div className={styles.announced} aria-live='polite' aria-atomic='true'>
                                    {announcementMessage}
                                </div>
                            ) : null}
                            {search ? <div tabIndex={0}>{search}</div> : null}
                            {clearSelection}
                        </div>
                    ) : null}
                    <div className={styles.filterItemsScrollable} aria-label='Filter Items Container'>
                        {list}
                    </div>
                    <div className={styles.footerContainer}>{footer}</div>
                </div>
            </PopoverSurface>
        </Popover>
    );
};

export default FilterPicker;
